/*
 * The AI read of a received fax, as Communication > Fax shows it (10-05).
 *
 * The server reads the first 5 pages of each new fax with the cheapest model
 * we run and keeps the result next to fax_master (v2 branch v2-fax-ai). Each
 * GET /v1/{pid}/fax/get-list row then carries it as `ai`:
 *
 *   ai: {
 *     status: "pending" | "done" | "error" | "skipped",
 *     doc_type: "referral",              // fixed list
 *     label: "Sleep study referral",     // <= 5 words; the pill
 *     summary: "...",                    // one line, no identifiers
 *     pages_total, pages_sent,
 *     match_status: "matched" | "multiple" | "possible" | "none",
 *     suggested_patient: { patientid, firstname, lastname, dob } | null,
 *     candidates: [{ patientid, firstname, lastname, dob }],   // "multiple"
 *   }
 *
 * A row without `ai` (an older v2, or a fax that was never read) shows
 * nothing. "error" and "skipped" show nothing either: staff read the PDF as
 * before. The patient is only ever a suggestion ("Possibly: ..."), a link to
 * that patient on the dashboard; nothing here files the fax to a chart. Send
 * to Athena (10-06) opens with that chart and a class from the label
 * pre-selected (faxAiAthenaClass, faxAiUniquePatient below); staff confirm
 * or change them, and only their Upload sends anything.
 *
 * Also read, for the stored column names: `fax_ai` for `ai`, and
 * match_patientid + pt_first/pt_last/pt_dob, match_candidates (array or JSON
 * text) in place of suggested_patient / candidates.
 *
 * What v2-fax-ai actually sends (api/ringcentral/practice/ai_view.py), and
 * how it is read here:
 *
 *   ai: { status: "pending" | "done" | "error",
 *         label: "sleep_study_report",          // slug -> prettified
 *         label_display: "Sleep study report",  // the pill when present
 *         summary,                              // 10-06 (prompt fax-v2): 2-3 sentences
 *         action: "Sign and return CPAP order by 10/10" | null,   // fax-v2: what the
 *                                               // clinic must do; null = nothing asked
 *         sender_name: "Walgreens" | null,      // 10-08 (v2-fax-list-sender): who sent
 *                                               // it, the row heading (faxSource below)
 *         pages_total, pages_sent,
 *         match_status: "unique" | "multiple" | "none" | "no_identity" | "unavailable",
 *         suggested_patientid, match_confidence }
 *
 * The list carries no patient name or DOB. For "unique" / "multiple" the
 * viewer asks GET /v1/{pid}/fax/{fax_id}/ai for the chart(s) when that fax
 * is opened (faxAiParts.jsx, faxApi.js faxAiDetail), and shows how close the
 * match is (faxAiMatchHint).
 */

export const FAX_AI_PENDING_TEXT = "Summarizing…";

// Re-read the list while a listed fax is still being read: once a minute, at
// most this many times for the same set of pending faxes (faxtable.jsx).
export const FAX_AI_POLL_MS = 60 * 1000;
export const FAX_AI_POLL_MAX = 5;

/*
 * Faxes the server has not read YET (10-08). The list row of a fax with no
 * fax_ai row comes back `ai: null`, not "pending": C-HIM's (9928) job runs
 * in the background AFTER the get-list that starts it (at most every 5
 * minutes, 10 faxes a run), and only for faxes whose PDF the hub has stored
 * (someone opened it). So the list that started the run never shows its
 * labels, and with no "pending" row nothing re-read the list: the labels
 * appeared only after leaving and re-opening the tab (owner, 10-08).
 *
 * For a practice whose profile says the server labels its faxes
 * (FAX_PROFILES aiLabels), a recent row with `ai: null` is "awaiting": the
 * list is re-read once a minute, at most FAX_AI_AWAIT_POLL_MAX times for the
 * same set (two job windows), and again when one of them is opened (that
 * open stores the PDF the job needs). Nothing is shown for an awaiting row:
 * an unopened 9928 fax may never be read. A row without `ai` (an older v2)
 * or older than the job's look-back (3 days) never counts.
 */
export const FAX_AI_AWAIT_POLL_MAX = 12;
export const FAX_AI_AWAIT_MAX_AGE_MS = 72 * 60 * 60 * 1000;

const PENDING = new Set(["pending", "queued", "processing", "running"]);
const DONE = new Set(["done", "completed", "complete", "ok", "success"]);
const NO_PATIENT = new Set(["possible", "none", "no_match", "nomatch", "no_identity", "unavailable"]);
const ONE_PATIENT = new Set(["matched", "unique"]);
const MAX_CANDIDATES = 3;
const SLUG = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

const text = (v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");

// The server stores "nothing asked" as null; also never show its spellings.
const NO_ACTION = /^(?:none|n\/?a|na|no action(?: needed| required)?|nothing(?: requested)?|-+)\.?$/i;
const actionOf = (ai) => {
  const a = text(ai.action);
  return a && !NO_ACTION.test(a) ? a : "";
};
const present = (v) => v !== undefined && v !== null && String(v).trim() !== "";

// "sleep_study_report" -> "Sleep study report"
const docTypeLabel = (t) => {
  const s = text(t).replace(/_/g, " ");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

// The pill: v2's label_display, else a label (prettified when it is a slug,
// as v2's `label` is), else the doc_type.
const labelOf = (ai) => {
  const shown = text(ai.label_display);
  if (shown) return shown;
  const label = text(ai.label);
  if (label) return SLUG.test(label) ? docTypeLabel(label) : label;
  return docTypeLabel(ai.doc_type);
};

const confidenceOf = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/*
 * How close the suggested chart is, in words (the server's confidence tiers,
 * api/ringcentral/practice/ai_match.py). Shown in grey after the link so
 * staff can weigh it before opening the chart. "" when unknown.
 */
export function faxAiMatchHint(confidence) {
  const c = confidenceOf(confidence);
  if (c === null) return "";
  if (c >= 1) return "name and DOB match";
  if (c >= 0.85) return "similar name, same DOB";
  if (c >= 0.75) return "first/last name swapped, same DOB";
  return "DOB day/month swapped";
}

const aiOf = (row) => {
  if (!row || typeof row !== "object") return null;
  const box = row.ai !== undefined && row.ai !== null ? row.ai : row.fax_ai;
  return box && typeof box === "object" && !Array.isArray(box) ? box : null;
};

// patient_master.dob holds both YYYY-MM-DD and MM/DD/YYYY: show MM/DD/YYYY.
export function formatFaxAiDob(dob) {
  const s = text(dob === undefined || dob === null ? "" : String(dob));
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T ])/.exec(s);
  if (m) return `${m[2].padStart(2, "0")}/${m[3].padStart(2, "0")}/${m[1]}`;
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
  return s;
}

const patientOf = (p) => {
  if (!p || typeof p !== "object" || !present(p.patientid)) return null;
  const name =
    text(p.name) ||
    text(p.patient_name) ||
    [text(p.firstname || p.first_name || p.first), text(p.lastname || p.last_name || p.last)]
      .filter(Boolean)
      .join(" ");
  if (!name) return null;
  return { patientid: p.patientid, name, dob: formatFaxAiDob(p.dob) };
};

const listOf = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// The chart(s) the server suggests: one when it matched one patient, up to
// three when several charts match (none is picked), else none.
const suggestionsOf = (ai) => {
  const status = text(ai.match_status).toLowerCase();
  if (NO_PATIENT.has(status)) return [];
  const one =
    patientOf(ai.suggested_patient) ||
    patientOf(ai.match) ||
    (present(ai.match_patientid)
      ? patientOf({ patientid: ai.match_patientid, name: ai.match_name, first: ai.pt_first, last: ai.pt_last, dob: ai.match_dob || ai.pt_dob })
      : null);
  if (one && status !== "multiple") return [one];
  const many = candidatesOf(ai.candidates !== undefined ? ai.candidates : ai.match_candidates);
  if (many.length) return many;
  return one ? [one] : [];
};

function candidatesOf(list) {
  const seen = new Set();
  const many = [];
  for (const c of listOf(list)) {
    const p = patientOf(c);
    if (!p || seen.has(String(p.patientid))) continue;
    seen.add(String(p.patientid));
    many.push(p);
  }
  return many.slice(0, MAX_CANDIDATES);
}

// v2 sends only ids in the list: the viewer fetches the chart(s) for these.
const detailWanted = (ai, status) =>
  status === "multiple" || (ONE_PATIENT.has(status) && present(ai.suggested_patientid));

/*
 * What to show for a list row:
 *   null                         nothing (no AI read, or it failed)
 *   { pending: true }            "Summarizing..."
 *   { pending: false, label, summary, action, suggestions: [{patientid, name, dob}],
 *     confidence, detail }       detail: the viewer must fetch the chart(s)
 *                                (v2 lists ids only), see faxAiDetailView;
 *                                action: "" when nothing is asked (or an older read)
 */
export function faxAiView(row) {
  const ai = aiOf(row);
  if (!ai) return null;
  const status = text(ai.status).toLowerCase();
  if (PENDING.has(status))
    return { pending: true, label: "", summary: "", action: "", suggestions: [], confidence: null, detail: false };
  if (status && !DONE.has(status)) return null;
  const label = labelOf(ai);
  const summary = text(ai.summary);
  const action = actionOf(ai);
  const suggestions = suggestionsOf(ai);
  const matchStatus = text(ai.match_status).toLowerCase();
  const detail = !suggestions.length && detailWanted(ai, matchStatus);
  if (!label && !summary && !action && !suggestions.length && !detail) return null;
  const confidence =
    matchStatus === "multiple" ? null : confidenceOf(ai.match_confidence !== undefined ? ai.match_confidence : ai.confidence);
  return { pending: false, label, summary, action, suggestions, confidence, detail };
}

/*
 * The chart(s) from GET /v1/{pid}/fax/{fax_id}/ai's `data` (v2 ai_view.detail):
 *   { suggestions: [{patientid, name, dob}], confidence } or null.
 * "unique": the one suggested chart (with its confidence); "multiple": up to
 * three candidates, none picked; anything else (or a chart the server could
 * not resolve): null, so nothing is shown.
 */
export function faxAiDetailView(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  if (!DONE.has(text(data.status).toLowerCase())) return null;
  const match = data.match;
  if (!match || typeof match !== "object") return null;
  const status = text(match.status).toLowerCase();
  if (ONE_PATIENT.has(status)) {
    const one = patientOf(match.suggested);
    return one ? { suggestions: [one], confidence: confidenceOf(match.confidence) } : null;
  }
  if (status === "multiple") {
    const many = candidatesOf(match.candidates);
    return many.length ? { suggestions: many, confidence: null } : null;
  }
  return null;
}

/*
 * Send to Athena pre-fill (10-06, sendtoathena.jsx). Only a pre-selection:
 * staff can change both, and nothing is sent until they press Upload.
 *
 * Class: the AI label picks the category the modal opens on, using only the
 * two upload routes that exist today:
 *   IMAGING   v1 fax-documents/add-imaging-result (filed closed, autoclose)
 *   CLINICAL  v1 fax-documents/add-clinical-document + documentsubclass
 * Everything but an imaging report is CLINICAL / CLINICALDOCUMENT, which
 * Athena routes for review (a DME order needs a provider to see it).
 * `other` and `records_request` get the same class without the "from the AI
 * label" hint: staff pick e.g. CONSULTNOTE or ADMISSIONDISCHARGE by hand.
 */
export const FAX_AI_ATHENA_CLASS = Object.freeze({
  imaging_report: { category: "IMAGING", subclass: null, hint: true },
  sleep_study_report: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  lab_result: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  referral: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  dme_cpap_order: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  prior_auth_insurance: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  prescription_refill: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  records_request: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: false },
  other: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: false },
});

// The label slug of a read fax (v2's `label`, else the older `doc_type`).
const labelSlugOf = (ai) => {
  for (const v of [ai.label, ai.doc_type]) {
    const s = text(v).toLowerCase();
    if (Object.prototype.hasOwnProperty.call(FAX_AI_ATHENA_CLASS, s)) return s;
  }
  return "";
};

/*
 * The class to open Send to Athena on for this fax:
 *   { category: "IMAGING" | "CLINICAL", subclass: "CLINICALDOCUMENT" | null,
 *     label: the pill text to cite ("" = no hint) }
 * or null (no finished read, or a label outside the list): the modal keeps
 * its own default.
 */
export function faxAiAthenaClass(row) {
  const view = faxAiView(row);
  if (!view || view.pending) return null;
  const slug = labelSlugOf(aiOf(row));
  if (!slug) return null;
  const c = FAX_AI_ATHENA_CLASS[slug];
  return { category: c.category, subclass: c.subclass, label: c.hint ? view.label || docTypeLabel(slug) : "" };
}

// The server matched the fax to exactly one chart ("unique" / "matched").
export function faxAiMatchIsUnique(row) {
  const view = faxAiView(row);
  if (!view || view.pending) return false;
  return ONE_PATIENT.has(text(aiOf(row).match_status).toLowerCase());
}

/*
 * The one chart to pre-select in Send to Athena, or null:
 *   { patientid, name, dob, confidence }
 * Only for a unique match, and only with a name to show (from the row, or
 * `detail` = faxAiDetailView of GET fax/{id}/ai for v2's id-only rows) whose
 * id is the row's suggested_patientid. Several charts, none, no identity or
 * an unresolved chart: null, so staff search as before. patient_master's id
 * is the Athena patient id for 9928 and 7825, so it is sent as is.
 */
export function faxAiUniquePatient(row, detail) {
  if (!faxAiMatchIsUnique(row)) return null;
  const view = faxAiView(row);
  const ai = aiOf(row);
  let one = view.suggestions.length === 1 ? view.suggestions[0] : null;
  let confidence = view.confidence;
  if (!one && detail && Array.isArray(detail.suggestions) && detail.suggestions.length === 1) {
    one = detail.suggestions[0];
    if (detail.confidence !== null && detail.confidence !== undefined) confidence = detail.confidence;
  }
  if (!one || !/^\d+$/.test(String(one.patientid)) || Number(one.patientid) <= 0) return null;
  if (present(ai.suggested_patientid) && String(ai.suggested_patientid) !== String(one.patientid)) return null;
  return { patientid: one.patientid, name: one.name, dob: one.dob, confidence };
}

// When the fax arrived (RingCentral's creationTime, else fax_master's
// created_on), in ms, or null.
const arrivedMs = (row) => {
  for (const v of [row.creationTime, row.created_on]) {
    if (typeof v !== "string" || !v.trim()) continue;
    const t = Date.parse(v.trim().replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, "$1T$2"));
    if (Number.isFinite(t)) return t;
  }
  return null;
};

const rowKey = (r) => String(r.faxid !== undefined ? r.faxid : r.id);

// A listed fax the server has not read yet (see FAX_AI_AWAIT_POLL_MAX).
export function faxAiAwaiting(row, now = Date.now()) {
  if (!row || typeof row !== "object") return false;
  if (!Object.prototype.hasOwnProperty.call(row, "ai") || row.ai !== null) return false;
  if (row.fax_ai !== undefined && row.fax_ai !== null) return false;
  const t = arrivedMs(row);
  return t !== null && now - t <= FAX_AI_AWAIT_MAX_AGE_MS;
}

// The row ids of listed faxes not read yet, as one key ("" when none).
export function faxAiAwaitingKey(rows, now = Date.now()) {
  return (rows || [])
    .filter((r) => faxAiAwaiting(r, now))
    .map(rowKey)
    .sort()
    .join(",");
}

// The row ids of listed faxes still being read, as one key ("" when none).
export function faxAiPendingKey(rows) {
  return (rows || [])
    .filter((r) => faxAiView(r)?.pending)
    .map(rowKey)
    .sort()
    .join(",");
}

/*
 * Who sent a fax: the row heading in the list and the viewer header (owner,
 * 10-08). RingCentral's caller-ID name used to be the heading, and for C-HIM
 * (9928) that is "Possible spam call" on thousands of faxes, a bare city and
 * state ("PECOS TX"), "PRIVATE" or a carrier ("WIRELESS CALLER"), while the AI
 * read already names the sender from the letterhead or cover sheet.
 *
 * The heading is, in order:
 *   1. the AI's sender (v2 list `ai.sender_name`, finished reads only), unless
 *      it is a placeholder ("Unknown", "N/A") or names this practice itself
 *      (a returned form on our own letterhead);
 *   2. the caller-ID name, unless it is a placeholder, a spam flag, a carrier
 *      or only a city and state;
 *   3. else the number, with "Source not identified yet" under it (just
 *      "Source not identified" once the AI has read it and found none), and a
 *      muted "Caller ID: PECOS TX" when the caller ID was a city and state.
 * The number stays on the second line under a name.
 */
export const FAX_SOURCE_UNKNOWN_TEXT = "Source not identified yet";
export const FAX_SOURCE_UNKNOWN_DONE_TEXT = "Source not identified";

const US_STATES =
  "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR GU VI";
const CALLER_PLACEHOLDER =
  /^(?:(?:possible|potential|suspected)\s+spam(?:\s+call(?:er)?)?|spam(?:\s+(?:risk|likely|call(?:er)?))?|scam\s+(?:likely|risk)|telemarketer|unknown(?:\s+(?:caller|name|sender|number))?|unavailable|anonymous|private(?:\s+(?:caller|number|name))?|restricted|blocked|no\s+caller\s*id|caller\s*id\s+(?:blocked|unavailable)|out\s+of\s+area|not\s+(?:available|provided|shown|printed)|none|n\/?a|fax|-+)\.?$/i;
const CALLER_PLACE = new RegExp(`^[a-z][a-z .'-]*\\s+(?:${US_STATES.split(" ").join("|")})$`, "i");
const CALLER_CARRIER =
  /^(?:wireless\s+caller|cell(?:ular)?\s+(?:phone|caller)|toll\s*free(?:\s+call(?:er)?)?|(?:t-?mobile|verizon(?:\s+wireless)?|at&t(?:\s+wireless)?|cingular(?:\s+wireless)?|sprint(?:\s+pcs)?|metro\s*pcs|cricket(?:\s+wireless)?|bandwidth(?:\.com)?|twilio|ringcentral|vonage|level\s*3|onvoy|inteliquent)(?:\s+(?:inc|llc|usa|wireless|caller))*\.?)$/i;

/*
 * A caller-ID name sorted for the heading:
 *   ""       nothing to show (empty, a spam flag, "PRIVATE", a carrier, digits)
 *   "place"  only a city and state ("PECOS TX"): a muted hint at most
 *   "name"   a real name ("WALGREENS PHARM"): usable as the heading
 */
export function faxCallerIdKind(name) {
  const n = text(name);
  if (!n || !/[a-z]/i.test(n) || CALLER_PLACEHOLDER.test(n) || CALLER_CARRIER.test(n)) return "";
  if (CALLER_PLACE.test(n)) return "place";
  return "name";
}

const squash = (s) => text(s).toLowerCase().replace(/[^a-z0-9]+/g, "");

// The AI's sender, when it names someone other than this practice.
const aiSenderOf = (ai, ownNames) => {
  if (!ai || !DONE.has(text(ai.status).toLowerCase())) return "";
  const s = text(ai.sender_name !== undefined && ai.sender_name !== null ? ai.sender_name : ai.sender);
  if (!s || !/[a-z]/i.test(s) || CALLER_PLACEHOLDER.test(s)) return "";
  const key = squash(s);
  if ((ownNames || []).some((n) => squash(n) && squash(n) === key)) return "";
  return s;
};

// "+14325822895" -> "(432) 582-2895"; anything else as it came.
export function formatFaxNumber(number) {
  const raw = text(number === undefined || number === null ? "" : String(number));
  const d = raw.replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d.length === 10 ? d : "";
  return ten && /^[+\d\s().-]+$/.test(raw) ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : raw;
}

/*
 * The heading of one fax:
 *   { heading,     bold line: the sender, else a usable caller-ID name, else
 *                  the number (else "Fax")
 *     identified,  true when the heading names the sender
 *     fromAi,      true when that name is the AI's
 *     number,      the formatted number ("" when none)
 *     sub,         the line under the heading: the number under a name, else
 *                  "Source not identified yet" / "Source not identified"
 *     callerHint,  "Caller ID: PECOS TX" (city/state caller ID, only when
 *                  nothing names the sender), else ""
 *     label }      the AI label pill text ("" when none)
 * ownNames: this practice's names (config/practice.js profile), never shown
 * as the sender of a fax it received.
 */
export function faxSource(row, ownNames = []) {
  const r = row && typeof row === "object" ? row : {};
  const ai = aiOf(r);
  const view = faxAiView(r);
  const label = view && !view.pending ? view.label : "";
  const number = formatFaxNumber(r.from?.phoneNumber);
  const callerName = text(r.from?.name);
  const callerKind = faxCallerIdKind(callerName);
  const aiSender = aiSenderOf(ai, ownNames);
  if (aiSender) return { heading: aiSender, identified: true, fromAi: true, number, sub: number, callerHint: "", label };
  if (callerKind === "name")
    return { heading: callerName, identified: true, fromAi: false, number, sub: number, callerHint: "", label };
  const readDone = !!ai && DONE.has(text(ai.status).toLowerCase());
  return {
    heading: number || "Fax",
    identified: false,
    fromAi: false,
    number,
    sub: readDone ? FAX_SOURCE_UNKNOWN_DONE_TEXT : FAX_SOURCE_UNKNOWN_TEXT,
    callerHint: callerKind === "place" ? `Caller ID: ${callerName}` : "",
    label,
  };
}

/*
 * The fax list search ("Search by phone, sender, or document type", fax.jsx):
 * the number (as stored, formatted, or digits only), the caller-ID name, the
 * AI's sender and label, and the subject.
 */
export function faxMatchesSearch(row, term) {
  const t = text(term).toLowerCase();
  if (!t) return true;
  const r = row && typeof row === "object" ? row : {};
  const ai = aiOf(r);
  const view = faxAiView(r);
  const phone = text(r.from?.phoneNumber);
  const hay = [
    phone,
    formatFaxNumber(phone),
    r.from?.name,
    ai && DONE.has(text(ai.status).toLowerCase()) ? ai.sender_name : "",
    view && !view.pending ? view.label : "",
    typeof r.subject === "string" ? r.subject : "",
  ]
    .map((v) => text(v).toLowerCase())
    .filter(Boolean);
  if (hay.some((h) => h.includes(t))) return true;
  // "432 582", "(432) 582-28": the digits of a phone-like term
  const digits = t.replace(/\D/g, "");
  return /^[\d\s()+.-]+$/.test(t) && digits.length >= 3 && phone.replace(/\D/g, "").includes(digits);
}
